import { useCallback, useState } from 'react';

import { logError } from '@zg-rentals/log-error';
import { useQueries } from '@zg-rentals/react-query5';

import { RentalManagerAccountViewZrmV2WebApiHandler_units } from '@zg-rentals/ts-rental-manager-api-beta-client';

import type { AccountViewMultiplexUnitsResponseDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

// UnitsParams is not exported from the API client, so it's copied here.
type UnitsParams = {
  groupId: string;
  includeActive?: boolean;
  includeArchived?: boolean;
  includeInactive?: boolean;
  includeUnarchived?: boolean;
  limit?: number;
  searchText?: string;
  startKeyExclusive?: string;
};

// Params accepted by the hook (e.g. transformFiltersToApiParams(filters)). Omit groupId and startKeyExclusive; those are supplied by the hook.
export type MultiplexUnitsListParams = Omit<UnitsParams, 'groupId' | 'startKeyExclusive'>;

function getUnitsApiParams(params: MultiplexUnitsListParams): Omit<UnitsParams, 'groupId' | 'startKeyExclusive'> {
  const apiParams: Omit<UnitsParams, 'groupId' | 'startKeyExclusive'> = {
    includeActive: params.includeActive,
    includeArchived: params.includeArchived,
    includeInactive: params.includeInactive,
    includeUnarchived: params.includeUnarchived,
    limit: params.limit,
    searchText: params.searchText,
  };

  if (!apiParams.searchText) delete apiParams.searchText;
  if (apiParams.includeActive === undefined) delete apiParams.includeActive;
  if (apiParams.includeInactive === undefined) delete apiParams.includeInactive;
  if (apiParams.includeArchived === undefined) delete apiParams.includeArchived;
  if (apiParams.includeUnarchived === undefined) delete apiParams.includeUnarchived;
  if (apiParams.limit === undefined) delete apiParams.limit;

  return apiParams;
}

export type UseGetMultiplexUnitsDataOptions = {
  enabled?: boolean;
};

/**
 * Fetches units for multiple group IDs in parallel with infinite-query style pagination per group.
 *
 * Pagination flow:
 * - We keep per group an array of startKeyExclusive values: [''], then ['', endKey1], etc.
 * - Each entry becomes one query (first page uses '', next pages use the previous response's pager.endKey).
 * - Calling fetchNextPageForGroup(groupId, pager.endKey) appends that key; on re-render we build one more
 *   query for that group, and useQueries runs it, so the next page is fetched.
 */
export const useGetMultiplexUnits = (params: MultiplexUnitsListParams, groupIds: Array<string>) => {
  const { includeActive, includeArchived, includeInactive, includeUnarchived, limit, searchText = '' } = params;

  // Per group: array of startKeyExclusive values. First is '' (first page); each additional is the previous page's pager.endKey.
  const [startKeysByGroupId, setStartKeysByGroupId] = useState<Record<string, Array<string>>>({});

  // One query per (groupId, startKeyExclusive). Adding a startKey causes one more query and thus one more fetch.
  const queries = groupIds.flatMap((groupId) => {
    const startKeys = startKeysByGroupId[groupId] ?? [''];
    return startKeys.map((startKeyExclusive) => ({
      queryKey: [
        'getMultiplexUnits',
        searchText,
        includeActive,
        includeInactive,
        includeArchived,
        includeUnarchived,
        limit,
        groupId,
        startKeyExclusive,
      ] as const,
      queryFn: async () => {
        if (!groupId?.trim()) {
          logError({
            error: new Error('useGetMultiplexUnits: groupId is required'),
            context: 'useGetMultiplexUnits',
          });

          throw new Error('groupId is required');
        }

        const apiParams: UnitsParams = {
          ...getUnitsApiParams(params),
          groupId,
          limit,
          startKeyExclusive: startKeyExclusive || undefined,
        };

        return RentalManagerAccountViewZrmV2WebApiHandler_units(apiParams);
      },
      enabled: !!groupId,
    }));
  });

  const results = useQueries({ queries });

  const dataByGroupId: Record<string, Partial<AccountViewMultiplexUnitsResponseDto>> = {};
  const hasNextPageByGroupId: Record<string, boolean> = {};
  const isFetchingNextPageByGroupId: Record<string, boolean> = {};
  const isFetchingByGroupId: Record<string, boolean> = {};

  // Merge all pages per group and derive hasNextPage / isFetchingNextPage / isFetchingByGroupId from results.
  let resultIndex = 0;
  for (const groupId of groupIds) {
    const startKeys = startKeysByGroupId[groupId] ?? [''];
    const groupResults = results.slice(resultIndex, resultIndex + startKeys.length);
    const pages = groupResults.map((r) => r.data).filter((d): d is AccountViewMultiplexUnitsResponseDto => d != null);
    if (pages.length > 0) {
      dataByGroupId[groupId] = {
        units: pages.flatMap((p) => p.units),
        listingCount: pages[0].listingCount,
        pager: pages[pages.length - 1].pager,
      };
      hasNextPageByGroupId[groupId] = pages[pages.length - 1].pager?.hasMore ?? false;
    }
    for (let p = 0; p < groupResults.length; p++) {
      if (groupResults[p]?.isFetching) {
        isFetchingByGroupId[groupId] = true;
        if (p >= 1) {
          isFetchingNextPageByGroupId[groupId] = true;
        }
        break;
      }
    }
    resultIndex += startKeys.length;
  }

  /**
   * Load the next page for a group. Appending nextStartKeyExclusive to state causes one more query
   * to be built on the next render, so useQueries runs it and the next page is fetched.
   * Pass the current last page's pager.endKey as nextStartKeyExclusive.
   */
  const fetchNextPageForGroup = useCallback((groupId: string, nextStartKeyExclusive: string) => {
    if (!nextStartKeyExclusive) return;
    setStartKeysByGroupId((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] ?? ['']), nextStartKeyExclusive],
    }));
  }, []);

  return {
    dataByGroupId,
    results,
    isPending: results.some((r) => r.isPending),
    isFetching: results.some((r) => r.isFetching),
    isFetchingByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingNextPageByGroupId,
  };
};
